import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  addSupportRequestComment,
  fetchSupportRequestComments,
  fetchSupportRequests,
  reopenSupportRequest
} from "../../Redux/support/action";
import {
  formatOpenedAt,
  isResolvedTicket,
  ticketPillFor
} from "../../Screens/Support/supportFormat";
import { SendIcon } from "../../Assets/assets";
import Button from "../Button/Button";
import PreviewBar from "../PreviewBar/PreviewBar";
import "./SupportTicketPreview.css";

// The original report — shown once, above the conversation, as a distinct
// card rather than a chat bubble: it carries structure (topic + optional
// attachment) a plain reply never does.
function ReportCard({ ticket }) {
  return (
    <div className="background-color-tertiaryColor2 border-radius-default padding-medium margin-bottom-large">
      <div className="font-color-tertiaryColor6 font-size-small text-transform-uppercase margin-bottom-small">
        {ticket.name}
      </div>
      {ticket.description ? (
        <div className="font-color-tertiaryColor font-size-medium white-space-pre-wrap">
          {ticket.description}
        </div>
      ) : null}
      {ticket.attachmentName ? (
        <div className="font-color-tertiaryColor6 font-size-small margin-top-small">
          Attachment: {ticket.attachmentName}
        </div>
      ) : null}
    </div>
  );
}

// One reply — reporter (you) trails right with no name line (alignment
// alone says "you"); support trails left under its own agent name line,
// falling back to a generic "Support" label when Plane has no actor (the
// system's own auto-reply).
function MessageBubble({ comment }) {
  const mine = comment.role === "reporter";
  // A support reply with no text (e.g. an attachment-only comment typed
  // directly in Plane) has nothing to show. A reporter's own comment is
  // always non-empty (validated server-side), so this only ever applies to
  // support's side.
  if (!mine && !comment.text?.trim()) {
    return null;
  }
  return (
    <div
      className={`display-flex flex-direction-column margin-bottom-default ${
        mine ? "flex-align-items-end" : "flex-align-items-start"
      }`}
    >
      {!mine ? (
        <div className="font-color-tertiaryColor6 font-size-smaller margin-bottom-smaller">
          {comment.agentName ? `${comment.agentName} · Support` : "Support"}
        </div>
      ) : null}
      <div
        className={`support-message-bubble padding-medium ${
          mine
            ? "border-radius-chat-bubble-mine background-color-secondary1"
            : "border-radius-chat-bubble-theirs background-color-grey"
        }`}
        data-cy={`SUPPORT-COMMENT-${comment.id}`}
      >
        <div
          className={`font-size-medium white-space-pre-wrap ${
            mine ? "font-color-tertiaryColor" : "font-color-dark"
          }`}
        >
          {comment.text}
        </div>
      </div>
    </div>
  );
}

// A ticket's conversation — mounted at /support/:workItemId from the Support
// screen. Reads the ticket from state.support.requests (already loaded by
// Support.js) and lazily loads/refreshes its own comment thread.
function SupportTicketPreview(props) {
  const navigate = useNavigate();
  const { workItemId } = useParams();
  const [reply, setReply] = useState("");

  const requests = props.requests;
  const ticket = requests.items.find((item) => item.workItemId === workItemId);
  const comments = props.comments[workItemId];

  useEffect(() => {
    if (!requests.loaded && !requests.loading) {
      fetchSupportRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Always refetches on open — support replies land in Plane directly (no
  // webhook/push into this app), so a cached thread from an earlier visit
  // this session can be stale.
  useEffect(() => {
    fetchSupportRequestComments(workItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workItemId]);

  const close = () => navigate("/support");

  if (!ticket) {
    return (
      <PreviewBar
        title="Support ticket"
        onCloseClick={close}
        loading={true}
        isMobileView={props.isMobileView}
      />
    );
  }

  const resolved = isResolvedTicket(ticket);
  const pill = ticketPillFor(
    ticket,
    comments?.items?.length ? comments.items[comments.items.length - 1] : null
  );
  const isReopening = props.reopeningWorkItemId === workItemId;

  const send = () => {
    const text = reply.trim();
    if (!text || comments?.sending) {
      return;
    }
    addSupportRequestComment(workItemId, text, props.profile?.data?.name);
    setReply("");
  };

  return (
    <>
      <PreviewBar
        title={
          <div className="display-flex flex-align-items-center">
            <div className="font-color-tertiaryColor font-size-larger">
              {ticket.name}
            </div>
            <div
              style={{ color: "var(--secondaryColor)" }}
              className="padding-left-default font-size-small text-uppercase"
            >
              {pill.label}
            </div>
          </div>
        }
        description={
          <div className="display-flex flex-align-items-center">
            <div>{ticket.ticketCode || formatOpenedAt(ticket.createdAt)}</div>
            {ticket.ticketCode ? (
              <>
                <div
                  style={{
                    height: "6px",
                    width: "6px",
                    margin: "0 6px",
                    borderRadius: "50%",
                    background: "var(--tertiaryColor5)"
                  }}
                />
                <div>{formatOpenedAt(ticket.createdAt)}</div>
              </>
            ) : null}
          </div>
        }
        onCloseClick={close}
        isMobileView={props.isMobileView}
        removeMargin={true}
      >
        <div className="inherit-parent-height display-flex flex-direction-column padding-horizontal-larger">
          <div
            style={{ minHeight: 0 }}
            className="flex-grow-1 overflow-scroll padding-top-larger"
          >
            <ReportCard ticket={ticket} />

            {!comments || (comments.loading && comments.items.length === 0) ? (
              <div className="text-align-center font-color-tertiaryColor6 font-size-smaller">
                Loading conversation…
              </div>
            ) : comments.items.length === 0 ? (
              <div className="text-align-center font-color-tertiaryColor6 font-size-smaller">
                No replies yet
              </div>
            ) : (
              comments.items.map((comment) => (
                <MessageBubble key={comment.id} comment={comment} />
              ))
            )}
          </div>

          <div
            style={{ flexShrink: 0 }}
            className="padding-top-large padding-bottom-larger"
          >
            {!resolved ? (
              <div
                className="display-flex flex-align-items-end"
                style={{ gap: "8px" }}
              >
                <textarea
                  style={{
                    border: "1px solid white",
                    borderRadius: "8px"
                  }}
                  className="support-reply-textarea inherit-parent-width font-color-tertiaryColor background-color-tertiaryColor2 border-radius-default font-size-medium padding-medium border-none"
                  placeholder="Reply to support…"
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  data-cy="SUPPORT-REPLY-INPUT"
                />
                <Button
                  variant="round"
                  text={<div></div>}
                  icon={<SendIcon color="#FFFFFF" />}
                  onClick={send}
                  disabled={!reply.trim() || Boolean(comments?.sending)}
                  loading={Boolean(comments?.sending)}
                  data-cy="SUPPORT-REPLY-SEND"
                />
              </div>
            ) : (
              <div className="">
                <div className="font-color-tertiaryColor6 font-size-small padding-bottom-large">
                  This ticket has been resolved. If it comes back, reopen rather
                  than raising a new ticket.
                </div>
                <Button
                  variant="primary"
                  text="Reopen"
                  onClick={() => reopenSupportRequest(workItemId)}
                  loading={isReopening}
                  data-cy="SUPPORT-REOPEN-TICKET"
                />
              </div>
            )}
          </div>
        </div>
      </PreviewBar>
    </>
  );
}

const mapStateToProps = (state) => ({
  requests: state.support.requests,
  comments: state.support.comments,
  profile: state.profile,
  reopeningWorkItemId: state.support.reopeningWorkItemId
});

export default connect(mapStateToProps)(SupportTicketPreview);
